// Utilidades básicas de nómina (Colombia, simplificadas)

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Devengados: salario + extras + bonificaciones + aux transporte (si aplica)
export function computeDevengados({
  baseSalary = 0,
  extras = 0,
  bonuses = 0,
  auxTransportEligible = false,
  auxTransport = 0,
}) {
  const aux = auxTransportEligible ? Number(auxTransport || 0) : 0;
  const salario = Number(baseSalary || 0);
  const devengados = {
    salario,
    extras: Number(extras || 0),
    bonificaciones: Number(bonuses || 0),
    auxTransporte: aux,
  };
  const gross = devengados.salario + devengados.extras + devengados.bonificaciones + devengados.auxTransporte;
  return { devengados, gross };
}

// IBC: salario + extras + bonificaciones (no incluye aux transporte)
export function computeIBC({
  baseSalary = 0,
  extras = 0,
  bonuses = 0,
  smmlv = 0,
  ibcMinMultiples = 1,
  ibcMaxMultiples = 25,
}) {
  const base = Number(baseSalary || 0) + Number(extras || 0) + Number(bonuses || 0);
  const min = Number(smmlv || 0) * Number(ibcMinMultiples || 1);
  const max = Number(smmlv || 0) * Number(ibcMaxMultiples || 25);
  return clamp(base, min, max);
}

// Deducciones del empleado: salud, pensión y FSP (si supera umbral)
export function computeEmployeeDeductions({
  ibc = 0,
  saludEmp = 0.04,
  pensionEmp = 0.04,
  fsp = { enabled: true, thresholdMultiples: 4, rate: 0.01, smmlv: 0 },
  otras = 0,
}) {
  const salud = Number(ibc) * Number(saludEmp || 0);
  const pension = Number(ibc) * Number(pensionEmp || 0);
  let fspEmp = 0;
  if (fsp && fsp.enabled && Number(ibc) > Number(fsp.thresholdMultiples || 4) * Number(fsp.smmlv || 0)) {
    fspEmp = Number(ibc) * Number(fsp.rate || 0);
  }
  const total = salud + pension + fspEmp + Number(otras || 0);
  return { salud, pension, fsp: fspEmp, otras: Number(otras || 0), total };
}

// Aportes del empleador (informativos)
export function computeEmployerContrib({ ibc = 0, employer = {} }) {
  const rate = (k) => Number(employer?.[k] || 0);
  const salud = ibc * rate('salud');
  const pension = ibc * rate('pension');
  const arl = ibc * rate('arl');
  const ccf = ibc * rate('ccf');
  const sena = ibc * rate('sena');
  const icbf = ibc * rate('icbf');
  const cesantias = ibc * rate('cesantias');
  const interesesCes = ibc * rate('interesesCes');
  const prima = ibc * rate('prima');
  const total = salud + pension + arl + ccf + sena + icbf + cesantias + interesesCes + prima;
  return { salud, pension, arl, ccf, sena, icbf, cesantias, interesesCes, prima, total };
}

// Cálculo integral
export function computePayroll({ settings, employee, period = {} }) {
  if (!settings?.enabled) {
    // Cálculo simple: salario + bonificaciones (sin deducciones)
    const baseSalary = Number(employee?.baseSalary || 0);
    const bonuses = Number(employee?.bonuses || 0);
    const { devengados, gross } = computeDevengados({ baseSalary, bonuses });
    return {
      devengados,
      gross,
      ibc: baseSalary + bonuses,
      deductions: { salud: 0, pension: 0, fsp: 0, otras: 0, total: 0 },
      employer: { total: 0 },
      neto: gross,
      meta: { mode: 'simple', period },
    };
  }

  const smmlv = Number(settings.smmlv || 0);
  const auxTransporte = Number(settings.auxTransporte || 0);
  const baseSalary = Number(employee?.baseSalary || 0);
  const extras = Number(period?.extras || 0);
  const bonuses = Number(period?.bonuses || 0);
  const auxEligible = baseSalary <= 2 * smmlv;

  const { devengados, gross } = computeDevengados({
    baseSalary,
    extras,
    bonuses,
    auxTransportEligible: auxEligible,
    auxTransport: auxTransporte,
  });

  const ibc = computeIBC({
    baseSalary,
    extras,
    bonuses,
    smmlv,
    ibcMinMultiples: settings.ibcMinMultiples ?? 1,
    ibcMaxMultiples: settings.ibcMaxMultiples ?? 25,
  });

  const deductions = computeEmployeeDeductions({
    ibc,
    saludEmp: settings.saludEmp ?? 0.04,
    pensionEmp: settings.pensionEmp ?? 0.04,
    fsp: { enabled: settings?.fsp?.enabled, thresholdMultiples: settings?.fsp?.thresholdMultiples ?? 4, rate: settings?.fsp?.rate ?? 0.01, smmlv },
  });

  const employer = computeEmployerContrib({ ibc, employer: settings.employer || {} });
  const neto = gross - deductions.total;

  return { devengados, gross, ibc, deductions, employer, neto, meta: { mode: 'co', period } };
}

export default {
  computeDevengados,
  computeIBC,
  computeEmployeeDeductions,
  computeEmployerContrib,
  computePayroll,
};

